import React, { useEffect, useState, useCallback } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import { Icon } from 'leaflet';
import { Link } from 'react-router-dom';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import { apiService } from '../../services/api';
import { MachineWithLocation } from '../../types';
import LoadingSpinner from '../common/LoadingSpinner';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { 
  ComputerDesktopIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  BuildingOfficeIcon,
  MagnifyingGlassIcon
} from '@heroicons/react/24/outline';

// Fix para ícones padrão do Leaflet
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});


// Componente para ajustar o zoom do mapa baseado nas máquinas
function MapBounds({ machines }: { machines: MachineWithLocation[] }) {
  const map = useMap();
  
  useEffect(() => {
    if (machines.length > 0) {
      const bounds = L.latLngBounds(
        machines.map(m => [m.latitude, m.longitude] as [number, number])
      );
      // Padding maior, mas permite zoom maior se necessário
      map.fitBounds(bounds, { 
        padding: [50, 50],
        maxZoom: 9 // Limita zoom máximo conforme solicitado
      });
    } else {
      // Centro do Brasil como padrão
      map.setView([-14.2350, -51.9253], 5);
    }
  }, [machines, map]);
  
  return null;
}


// Componente para fazer zoom quando um marcador é clicado
function ZoomToMarker({ machine }: { machine: MachineWithLocation | null }) {
  const map = useMap();
  
  useEffect(() => {
    if (machine) {
      // Zoom para a máquina
      map.setView([machine.latitude, machine.longitude], 10, { 
        animate: true,
        duration: 0.8
      });
      
      // Abrir popup após o zoom
      setTimeout(() => {
        map.eachLayer((layer: any) => {
          if (layer instanceof L.Marker) {
            const latlng = layer.getLatLng();
            if (latlng.lat === machine.latitude && latlng.lng === machine.longitude) {
              layer.openPopup();
            }
          }
        });
      }, 900);
    }
  }, [machine, map]);
  
  return null;
}

// Componente para detectar cliques no mapa (fora dos marcadores)
function MapClickHandler({ 
  onMapClick 
}: { 
  onMapClick: () => void;
}) {
  const map = useMap();
  
  useEffect(() => {
    const handleClick = (e: L.LeafletMouseEvent) => {
      // Verificar se o clique foi em um marcador
      let clickedOnMarker = false;
      map.eachLayer((layer: any) => {
        if (layer instanceof L.Marker) {
          const markerPoint = map.latLngToContainerPoint(layer.getLatLng());
          const clickPoint = map.mouseEventToContainerPoint(e.originalEvent);
          const distance = Math.sqrt(
            Math.pow(markerPoint.x - clickPoint.x, 2) + 
            Math.pow(markerPoint.y - clickPoint.y, 2)
          );
          // Se o clique foi perto de um marcador (dentro de 20px), não deselecionar
          if (distance < 20) {
            clickedOnMarker = true;
          }
        }
      });
      
      // Se o clique foi no mapa (não em um marcador), deselecionar
      if (!clickedOnMarker) {
        onMapClick();
      }
    };
    
    map.on('click', handleClick);
    
    return () => {
      map.off('click', handleClick);
    };
  }, [map, onMapClick]);
  
  return null;
}

interface MachinesMapProps {
  className?: string;
  tenantId?: string; // Optional filter by tenant ID
  initialMachineId?: string; // Optional initial machine to select
}

export default function MachinesMap({ className = '', tenantId, initialMachineId }: MachinesMapProps) {
  const { isDark } = useTheme();
  const { state } = useAuth();
  const isSuperAdmin = state.user?.role === 'super_admin';
  
  const [machines, setMachines] = useState<MachineWithLocation[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [selectedMachine, setSelectedMachine] = useState<MachineWithLocation | null>(null);
  const [hoveredMachine, setHoveredMachine] = useState<string | null>(null);
  const [selectedBranch, setSelectedBranch] = useState<string>('');
  const [filteredMachines, setFilteredMachines] = useState<MachineWithLocation[]>([]);
  const [branchStats, setBranchStats] = useState<Array<{
    branch_id: string | null;
    branch_name: string;
    total: number;
    online: number;
    offline: number;
  }>>([]);
  const [branchSearch, setBranchSearch] = useState<string>('');
  const [isLoadingBranchStats, setIsLoadingBranchStats] = useState(false);

  const loadMachines = useCallback(async () => {
    try {
      setIsLoading(true);
      setError('');
      const data = await apiService.getMachinesForMap(tenantId);
      setMachines(data);
      
      // Select initial machine if provided
      if (initialMachineId && data.length > 0) {
        const machine = data.find(m => m.id === initialMachineId);
        if (machine) {
          setSelectedMachine(machine);
        }
      }
    } catch (err: any) {
      setError('Erro ao carregar localizações das máquinas');
      console.error('Machines map error:', err);
    } finally {
      setIsLoading(false);
    }
  }, [tenantId, initialMachineId]);

  const loadBranchStats = useCallback(async () => {
    try {
      setIsLoadingBranchStats(true);
      // Super admin needs a tenant selected to load branch stats
      if (isSuperAdmin && !tenantId) {
        setBranchStats([]);
        return;
      }
      
      const response = await apiService.getMachinesByBranch();
      if (response.success) {
        setBranchStats(response.data);
      }
    } catch (err) {
      console.error('Erro ao carregar estatísticas de filiais:', err);
      setBranchStats([]);
    } finally {
      setIsLoadingBranchStats(false);
    }
  }, [isSuperAdmin, tenantId]);

  useEffect(() => {
    loadMachines();
    loadBranchStats();
  }, [loadMachines, loadBranchStats]);

  // Update selected machine when initialMachineId changes
  useEffect(() => {
    if (initialMachineId && machines.length > 0) {
      const machine = machines.find(m => m.id === initialMachineId);
      if (machine) {
        setSelectedMachine(machine);
      }
    }
  }, [initialMachineId, machines]);

  // Filter machines by branch
  useEffect(() => {
    if (selectedBranch) {
      const filtered = machines.filter(m => m.branch === selectedBranch);
      setFilteredMachines(filtered);
    } else {
      // Quando nenhuma filial estiver selecionada, mostrar todas as máquinas
      setFilteredMachines(machines);
    }
  }, [selectedBranch, machines]);

  // Filter branch stats by search
  const filteredBranchStats = branchStats.filter(stat => 
    stat.branch_name.toLowerCase().includes(branchSearch.toLowerCase())
  );

  // Prepare chart data
  const chartData = filteredBranchStats.map(stat => ({
    name: stat.branch_name,
    total: stat.total,
    online: stat.online,
    offline: stat.offline,
  })).sort((a, b) => b.total - a.total);

  const handleBranchClick = (branchName: string) => {
    setSelectedBranch(branchName);
    setBranchSearch(branchName);
  };

  const getStatusIcon = (status: string, machineId: string, isHovered: boolean) => {
    // Tamanho normal: 25x41, tamanho expandido: 37x61 (1.5x)
    const scale = isHovered ? 1.5 : 1;
    const iconSize: [number, number] = [25 * scale, 41 * scale];
    const iconAnchor: [number, number] = [12.5 * scale, 41 * scale];
    
    let color = '#3388ff';
    
    switch (status) {
      case 'online':
        color = '#10B981';
        break;
      case 'offline':
        color = '#EF4444';
        break;
      case 'warning':
        color = '#F59E0B';
        break;
    }
    
    return new Icon({
      iconUrl: `data:image/svg+xml;base64,${btoa(`
        <svg xmlns="http://www.w3.org/2000/svg" width="${25 * scale}" height="${41 * scale}" viewBox="0 0 25 41">
          <path fill="${color}" stroke="#fff" stroke-width="1.5" d="M12.5 0C5.6 0 0 5.6 0 12.5 0 21.8 12.5 41 12.5 41S25 21.8 25 12.5C25 5.6 19.4 0 12.5 0z"/>
          <circle fill="#fff" cx="12.5" cy="12.5" r="6"/>
        </svg>
      `)}`,
      iconSize: iconSize,
      iconAnchor: iconAnchor,
      popupAnchor: [0, -41 * scale],
      className: isHovered ? 'marker-hovered' : '',
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'online':
        return 'text-green-600 bg-green-100 dark:bg-green-900/30 dark:text-green-400';
      case 'offline':
        return 'text-red-600 bg-red-100 dark:bg-red-900/30 dark:text-red-400';
      case 'warning':
        return 'text-yellow-600 bg-yellow-100 dark:bg-yellow-900/30 dark:text-yellow-400';
      default:
        return 'text-gray-600 bg-gray-100 dark:bg-gray-700 dark:text-gray-400';
    }
  };

  const getStatusIconComponent = (status: string) => {
    switch (status) {
      case 'online':
        return <CheckCircleIcon className="w-4 h-4 text-green-600" />;
      case 'offline':
        return <XCircleIcon className="w-4 h-4 text-red-600" />;
      case 'warning':
        return <ExclamationTriangleIcon className="w-4 h-4 text-yellow-600" />;
      default:
        return <ComputerDesktopIcon className="w-4 h-4 text-gray-600" />;
    }
  };

  if (isLoading) {
    return (
      <div className={`flex items-center justify-center h-96 ${className}`}>
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className={`bg-red-50 border border-red-200 rounded-md p-4 dark:bg-red-900/20 dark:border-red-800 ${className}`}>
        <div className="text-red-700 dark:text-red-400">{error}</div>
        <button
          onClick={loadMachines}
          className="mt-2 text-red-600 hover:text-red-800 underline dark:text-red-400 dark:hover:text-red-300"
        >
          Tentar novamente
        </button>
      </div>
    );
  }

  if (machines.length === 0) {
    return (
      <div className={`flex items-center justify-center h-96 text-gray-500 dark:text-gray-400 ${className}`}>
        <p>Nenhuma máquina com localização cadastrada</p>
      </div>
    );
  }

  return (
    <div className={className}>
      {/* Mapa */}
      <div className="relative" style={{ height: '600px', zIndex: 0 }}>
        <MapContainer
          center={[-14.2350, -51.9253]} // Centro do Brasil
          zoom={4}
          minZoom={3}
          maxZoom={9}
          style={{ height: '100%', width: '100%' }}
          scrollWheelZoom={true}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url='https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'
          />
          <MapBounds machines={filteredMachines.length > 0 ? filteredMachines : machines} />
          <ZoomToMarker machine={selectedMachine} />
          <MapClickHandler onMapClick={() => {
            setSelectedMachine(null);
            setHoveredMachine(null);
          }} />
          {(filteredMachines.length > 0 ? filteredMachines : machines).map((machine) => {
            const isHovered = hoveredMachine === machine.id;
            const isSelected = selectedMachine?.id === machine.id;
            return (
            <Marker
              key={`${machine.id}-${isHovered || isSelected ? 'hovered' : 'normal'}`}
              position={[machine.latitude, machine.longitude]}
              icon={getStatusIcon(machine.status, machine.id, isHovered || isSelected)}
              zIndexOffset={(isHovered || isSelected) ? 1000 : 0}
              eventHandlers={{
                click: () => {
                  setSelectedMachine(machine);
                  // Manter hover quando selecionado para destacar visualmente
                  setHoveredMachine(machine.id);
                },
                mouseover: () => {
                  // Mostrar hover apenas se não houver outra máquina selecionada
                  if (!selectedMachine || selectedMachine.id === machine.id) {
                    setHoveredMachine(machine.id);
                  }
                },
                mouseout: () => {
                  // Não limpar hover se esta é a máquina selecionada
                  if (!selectedMachine || selectedMachine.id !== machine.id) {
                    setHoveredMachine(null);
                  }
                }
              }}
            >
              <Popup 
                eventHandlers={{
                  add: () => {
                    setSelectedMachine(machine);
                  }
                }}
                className={`custom-popup ${isDark ? 'dark-popup' : ''}`}
              >
                <div className={`min-w-[250px] p-2 ${isDark ? 'dark:bg-gray-800 dark:text-gray-100' : 'bg-white text-gray-900'}`}>
                  <div className="flex items-center justify-between mb-3">
                    <h3 className={`text-lg font-semibold ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>
                      {machine.name}
                    </h3>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(machine.status)}`}>
                      {machine.status}
                    </span>
                  </div>
                  
                  <div className={`space-y-2 text-sm ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>
                    <div className={`flex items-center ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>
                      <ComputerDesktopIcon className={`w-4 h-4 mr-2 ${isDark ? 'text-gray-100' : 'text-gray-600'}`} />
                      <span className={isDark ? 'text-gray-100' : 'text-gray-900'}>{machine.agent_id}</span>
                    </div>
                    
                    {machine.user_name && (
                      <div className={`flex items-center ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>
                        <span className="mr-2">👤</span>
                        <span className={isDark ? 'text-gray-100' : 'text-gray-900'}>{machine.user_name}</span>
                      </div>
                    )}
                    
                    {machine.branch && (
                      <div className={`flex items-center ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>
                        <span className="mr-2">🏢</span>
                        <span className={isDark ? 'text-gray-100' : 'text-gray-900'}>{machine.branch}</span>
                      </div>
                    )}
                    
                    {machine.tenant_name && (
                      <div className={`flex items-center ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>
                        <span className="mr-2">🏛️</span>
                        <span className={isDark ? 'text-gray-100' : 'text-gray-900'}>{machine.tenant_name}</span>
                      </div>
                    )}
                    
                    {machine.address && (
                      <div className={`flex items-start ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>
                        <span className="mr-2">📍</span>
                        <span className={`text-xs ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>{machine.address}</span>
                      </div>
                    )}
                    
                    {machine.last_seen && (
                      <div className={`flex items-center text-xs ${isDark ? 'text-gray-300' : 'text-gray-500'}`}>
                        <span className="mr-2">🕒</span>
                        <span className={isDark ? 'text-gray-300' : 'text-gray-500'}>Última conexão: {new Date(machine.last_seen.endsWith('Z') ? machine.last_seen : machine.last_seen + 'Z').toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo' })}</span>
                      </div>
                    )}
                    
                    {machine.observation && (
                      <div className={`flex items-start mt-2 ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>
                        <span className="mr-2">📝</span>
                        <div className="flex-1">
                          <span className={`text-xs font-medium ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>Observação: </span>
                          <span className={`text-xs ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>{machine.observation}</span>
                        </div>
                      </div>
                    )}
                    
                    <div className="pt-2 border-t border-gray-200 dark:border-gray-700 mt-3">
                      <Link
                        to={`/machines/${machine.id}`}
                        className="popup-ver-detalhes inline-flex items-center justify-center w-full px-4 py-2 text-sm font-medium bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 dark:bg-blue-500 dark:hover:bg-blue-600 !text-white hover:!text-white"
                      >
                        Ver Detalhes
                      </Link>
                    </div>
                  </div>
                </div>
              </Popup>
            </Marker>
            );
          })}
        </MapContainer>
        <style>{`
          .leaflet-marker-icon {
            transition: transform 0.2s ease-in-out;
          }
          .marker-hovered {
            transform: scale(1.5);
            z-index: 1000 !important;
          }
          .popup-ver-detalhes,
          .popup-ver-detalhes:hover {
            color: #fff !important;
          }
        `}</style>
      </div>
      
      {/* Gráfico de Filiais e Filtro */}
      <div className={`mt-3 space-y-3 ${isDark ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow p-3`}>
        <div>
          <label htmlFor="branch-search" className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5">
            <BuildingOfficeIcon className="w-3.5 h-3.5 inline mr-1.5" />
            Filtrar por Filial
          </label>
          {isSuperAdmin && !tenantId ? (
            <div className="block w-full px-3 py-2 border border-yellow-300 dark:border-yellow-600 rounded-md bg-yellow-50 dark:bg-yellow-900/20 text-yellow-800 dark:text-yellow-300">
              Selecione uma empresa no mapa para ver suas filiais
            </div>
          ) : (
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <MagnifyingGlassIcon className="h-4 w-4 text-gray-400" />
              </div>
              <input
                id="branch-search"
                type="text"
                value={branchSearch}
                onChange={(e) => {
                  setBranchSearch(e.target.value);
                  if (!e.target.value) {
                    setSelectedBranch('');
                  }
                }}
                placeholder="Pesquisar filial..."
                className="block w-full pl-8 pr-8 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
              {(branchSearch || selectedBranch) ? (
                <button
                  type="button"
                  onClick={() => {
                    setBranchSearch('');
                    setSelectedBranch('');
                  }}
                  className="absolute inset-y-0 right-0 flex items-center pr-2.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 text-lg leading-none"
                  title="Limpar filial"
                >
                  ×
                </button>
              ) : null}
            </div>
          )}
        </div>

        {/* Gráfico de Barras Horizontais */}
        {!isSuperAdmin || tenantId ? (
          <div>
            <h3 className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">
              Máquinas por Filial
            </h3>
            {isLoadingBranchStats ? (
              <div className="flex items-center justify-center h-48">
                <LoadingSpinner size="md" />
              </div>
            ) : chartData.length > 0 ? (
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={chartData}
                    layout="vertical"
                    margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke={isDark ? "#374151" : "#e5e7eb"} />
                    <XAxis type="number" stroke={isDark ? "#9CA3AF" : "#6B7280"} />
                    <YAxis 
                      dataKey="name" 
                      type="category" 
                      width={120}
                      stroke={isDark ? "#9CA3AF" : "#6B7280"}
                      tick={{ fontSize: 10 }}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: isDark ? "#1f2937" : "#ffffff",
                        border: isDark ? "1px solid #374151" : "1px solid #e5e7eb",
                        borderRadius: "0.375rem",
                        color: isDark ? "#fff" : "#1f2937",
                      }}
                      formatter={(value: number, name: string) => {
                        if (name === 'online') return [`${value} Online`, 'Online'];
                        if (name === 'offline') return [`${value} Offline`, 'Offline'];
                        return [value, name];
                      }}
                    />
                    <Legend 
                      wrapperStyle={{ paddingTop: '20px' }}
                      formatter={(value) => {
                        if (value === 'online') return 'Online';
                        if (value === 'offline') return 'Offline';
                        return value;
                      }}
                    />
                    <Bar 
                      dataKey="online" 
                      stackId="a" 
                      fill="#10B981"
                      onClick={(data: any) => handleBranchClick(data.name)}
                      style={{ cursor: 'pointer' }}
                    >
                      {chartData.map((entry, index) => (
                        <Cell 
                          key={`cell-online-${index}`} 
                          fill={selectedBranch === entry.name ? "#059669" : "#10B981"}
                        />
                      ))}
                    </Bar>
                    <Bar 
                      dataKey="offline" 
                      stackId="a" 
                      fill="#EF4444"
                      onClick={(data: any) => handleBranchClick(data.name)}
                      style={{ cursor: 'pointer' }}
                    >
                      {chartData.map((entry, index) => (
                        <Cell 
                          key={`cell-offline-${index}`} 
                          fill={selectedBranch === entry.name ? "#DC2626" : "#EF4444"}
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <p className="text-xs text-gray-500 dark:text-gray-400 text-center py-4">
                Nenhuma filial com máquinas encontrada
              </p>
            )}
          </div>
        ) : null}

        {/* Lista de Máquinas */}
        {filteredMachines.length > 0 && (
          <div>
            <h3 className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5">
              {selectedBranch 
                ? `Máquinas da Filial: ${selectedBranch} (${filteredMachines.length})`
                : `Todas as Máquinas (${filteredMachines.length})`
              }
            </h3>
            <div className={`max-h-48 overflow-y-auto border ${isDark ? 'border-gray-700' : 'border-gray-200'} rounded`}>
              <ul className="divide-y divide-gray-200 dark:divide-gray-700">
                {filteredMachines.map((machine) => (
                  <li
                    key={machine.id}
                    onClick={() => {
                      setSelectedMachine(machine);
                    }}
                    className={`p-2 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer transition-colors ${
                      selectedMachine?.id === machine.id ? 'bg-blue-50 dark:bg-blue-900/20' : ''
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center">
                          {getStatusIconComponent(machine.status)}
                          <span className={`ml-1.5 text-xs font-medium ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>
                            {machine.name}
                          </span>
                          {machine.branch && (
                            <span className="ml-1.5 text-xs text-gray-500 dark:text-gray-400 flex items-center">
                              <BuildingOfficeIcon className="w-3 h-3 mr-0.5" /> {machine.branch}
                            </span>
                          )}
                        </div>
                        {machine.user_name && (
                          <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'} mt-0.5`}>
                            {machine.user_name}
                          </p>
                        )}
                      </div>
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getStatusColor(machine.status)}`}>
                        {machine.status}
                      </span>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}
        
        {selectedBranch && filteredMachines.length === 0 && (
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Nenhuma máquina encontrada para a filial "{selectedBranch}"
          </p>
        )}
      </div>
    </div>
  );
}

